import { useState } from 'react';
import ArrowLeft from 'svg/ArrowLeft';
import CloseIcon from 'svg/CloseIcon';
import { Button, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import GeoLocationInitializer from 'components/utilities/GeoLocationInitializer';
import classNames from 'styles/utils/classNames';
import AddressField from './components/AddressField';
import ManualAddressForm from './components/ManualAddressForm';
import ManualAddressPrompt from './components/ManualAddressPrompt';
import SelectionPreview from './components/SelectionPreview';
import VenueResults from './components/VenueResults';
import type { AddVenueProps } from './props';
import { useAddVenue } from './useAddVenue';

type ContentProps = Omit<AddVenueProps, 'isOpen'>;

// Separate from the shell so the search queries only run while the modal is open.
function AddVenueContent({
  title,
  onClose,
  onSubmit,
  onSubmitManualAddress,
  enableManualAddress = false,
}: ContentProps) {
  const state = useAddVenue({ onSubmit, onSubmitManualAddress });
  const [isListScrolled, setIsListScrolled] = useState(false);

  const { isManual, isPreviewStep } = state;
  const isManualEntryStep = isManual && !isPreviewStep;
  const hasMobileFooter = isManual || isPreviewStep;

  return (
    <div className="flex h-[calc(100dvh-1.5rem)] flex-col sm:h-[min(49.75rem,calc(100dvh-2rem))] lg:h-auto">
      <header className="flex shrink-0 items-center justify-between border-color-border-input-lightmode p-ds-lg dark:border-color-border-input-darkmode lg:h-20 lg:border-b lg:px-ds-3xl lg:py-5">
        <h2 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {title}
        </h2>
        <ButtonText
          size="icon"
          aria-label="Close"
          className="flex h-8 w-8 items-center justify-center rounded-full text-color-text-lightmode-primary dark:text-color-text-darkmode-primary lg:h-10 lg:w-10"
          onClick={onClose}
        >
          <CloseIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
        </ButtonText>
      </header>

      <div className="flex min-h-0 flex-1 lg:h-[29.5rem] lg:flex-none">
        <section
          className={classNames('flex min-w-0 flex-1 flex-col', isPreviewStep && 'max-lg:hidden')}
        >
          {isManual ? (
            <ManualAddressForm
              manualSearch={state.manualSearch}
              handleManualInput={state.handleManualInput}
              exitManualMode={state.exitManualMode}
            />
          ) : (
            <>
              <div
                className={classNames(
                  'relative z-10 shrink-0 px-ds-lg py-ds-md transition-shadow lg:px-ds-3xl lg:py-ds-lg',
                  isListScrolled && 'shadow-[0px_4px_8px_rgba(0,0,0,0.08)]',
                )}
              >
                <AddressField
                  id="add-venue-search"
                  variant="pill"
                  search={state.venueSearch}
                  placeholder={state.searchPlaceholder}
                />
              </div>
              <div
                className="flex min-h-0 flex-1 flex-col overflow-y-auto px-ds-lg pb-ds-lg pt-ds-md lg:px-ds-3xl lg:pb-ds-3xl"
                onScroll={(e) => setIsListScrolled(e.currentTarget.scrollTop > 0)}
              >
                <VenueResults
                  venues={state.venues}
                  isVenuesLoading={state.isVenuesLoading}
                  hasSearchCenter={state.hasSearchCenter}
                  selectedSlug={state.selectedSlug}
                  selectVenue={state.selectVenue}
                />
                {enableManualAddress && (
                  <ManualAddressPrompt onEnterManually={state.enterManualMode} />
                )}
              </div>
            </>
          )}
        </section>

        <aside
          className={classNames(
            'flex min-w-0 overflow-y-auto max-lg:flex-1 max-lg:px-ds-lg max-lg:pt-ds-lg lg:w-[44.2623%] lg:shrink-0 lg:bg-color-bg-lightmode-secondary lg:px-ds-3xl lg:py-ds-lg dark:lg:bg-color-bg-darkmode-secondary',
            !isPreviewStep && 'max-lg:hidden',
          )}
        >
          <div className="w-full lg:m-auto lg:max-w-[27rem]">
            <SelectionPreview
              isManual={isManual}
              selectedVenue={state.selectedVenue}
              selectedImageUrl={state.selectedImageUrl}
              isVenueLoading={state.isVenueLoading}
              clearVenue={state.clearVenue}
              manualAddress={state.manualAddress}
            />
          </div>
        </aside>
      </div>

      <footer
        className={classNames(
          'shrink-0 border-color-border-input-lightmode p-ds-lg dark:border-color-border-input-darkmode lg:border-t lg:px-ds-3xl lg:pt-[0.9375rem]',
          !hasMobileFooter && 'max-lg:hidden',
        )}
      >
        <div className="flex gap-ds-lg lg:hidden">
          <Button
            variant="secondary"
            size="lg"
            className="flex-1"
            iconLeft={<ArrowLeft className="h-5 w-5" />}
            onClick={state.goBack}
          >
            Go back
          </Button>
          <Button
            variant="primary"
            size="lg"
            className="flex-1"
            disabled={!state.canSubmit}
            onClick={isManualEntryStep ? state.showPreviewStep : state.submit}
          >
            {isManualEntryStep ? 'Preview' : isManual ? 'Add address' : title}
          </Button>
        </div>
        <div className="flex justify-end max-lg:hidden">
          <Button
            variant="primary"
            size="lg"
            isInline
            disabled={!state.canSubmit}
            onClick={state.submit}
          >
            {isManual ? 'Add custom address' : title}
          </Button>
        </div>
      </footer>
    </div>
  );
}

export default function AddVenue({ isOpen, ...props }: AddVenueProps) {
  return (
    <Modal
      isOpen={isOpen}
      handleClose={props.onClose}
      classNameMaxWidth="sm:max-w-md lg:max-w-[76.25rem]"
      classNameRounded="rounded-t-3xl sm:rounded-3xl"
      className="lg:w-[calc(100%-3rem)] lg:shadow-[0px_8px_24px_rgba(0,0,0,0.25)]"
    >
      {/* The app-wide auto-init skips the location estimate; this runs the full resolve. */}
      <GeoLocationInitializer>
        <AddVenueContent {...props} />
      </GeoLocationInitializer>
    </Modal>
  );
}
